import { ApolloClient, ApolloLink, split, createHttpLink } from '@apollo/client'
import { InMemoryCache } from '@apollo/client/cache'
import { setContext } from '@apollo/client/link/context'
import { WebSocketLink } from '@apollo/client/link/ws'
import { getMainDefinition } from '@apollo/client/utilities'
import { createNetworkStatusNotifier } from 'react-apollo-network-status'

import { GetTokenSilentlyOptions } from '@auth0/auth0-spa-js/dist/typings/global'
import generatedIntrospection from '../../generated/fragmentTypes.json'

const studentApiUri = import.meta.env.VITE_STUDENT_API_GQL_URI
const studentWsUri = import.meta.env.VITE_STUDENT_API_WS_URI

const { link: statusLink, useApolloNetworkStatus } = createNetworkStatusNotifier()

export const useStudentNetworkStatus = useApolloNetworkStatus

const getStudentClient = (
  getTokenSilently: (o?: GetTokenSilentlyOptions | undefined) => Promise<string | undefined>,
  isAuthenticated: boolean = true
) => {
  const authLink = setContext(async (_, { headers, isPublicQuery }) => {
    if (isPublicQuery) {
      return { headers }
    }
    const accessToken = await getTokenSilently()
    return {
      headers: {
        ...headers,
        authorization: accessToken ? `Bearer ${accessToken}` : ''
      }
    }
  })

  const http = createHttpLink({
    uri: studentApiUri
  })

  const wsLink = isAuthenticated
    ? new WebSocketLink({
        uri: studentWsUri!,
        options: {
          reconnect: true,
          connectionParams: async () => {
            const accessToken = await getTokenSilently()
            return accessToken ? { Authorization: `Bearer ${accessToken}` } : {}
          },
          lazy: true
        }
      })
    : null

  const splitLink = isAuthenticated
    ? split(
        ({ query }) => {
          const definition = getMainDefinition(query)
          return (
            definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
          )
        },
        wsLink!,
        http
      )
    : http

  const link = ApolloLink.from([authLink, statusLink, splitLink])

  const cache = new InMemoryCache({
    possibleTypes: generatedIntrospection.possibleTypes,
    typePolicies: {
      PlanAlert: {
        keyFields: ['alertKey', 'itemId']
      },
      PlanGroup: {
        fields: {
          items: {
            merge(_existing, incoming: any[]) {
              return incoming
            }
          }
        }
      },
      AdditionalAuditReportType: {
        keyFields: ['auditReportType', 'careerCode']
      }
    }
  })

  const studentClient = new ApolloClient({
    link,
    cache,
    connectToDevTools: import.meta.env.MODE !== 'production'
  })

  return studentClient
}

export default getStudentClient
